// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { difference, isEqual } from 'lodash';

// Utils
import rIC from '../../common/utils/lib/rIC';
import { hasChanged, noLonger, now } from '../utils/react/propsComparisons';

const childElementIdsHaveChanged = hasChanged('childElementIds');
const batchedRenderNoLongerInProgress = noLonger('isBatchedRenderInProgress');
const batchedRenderNowInProgress = now('isBatchedRenderInProgress');

const MAX_ITERATIONS = 80;
const EMPTY_ARRAY = [];
const DEFAULT_RENDER_INCREMENT = 20;

class OrchestratedList extends React.Component {
    initialRenderElementIds = EMPTY_ARRAY;

    state = {
        isBatchedRenderInProgress: true,
        elementIdsToRender: EMPTY_ARRAY,
    };

    componentDidMount() {
        if (this.props.skipOrchestration) return;

        this.startBatchedRender();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.skipOrchestration) return;

        const { isBatchedRenderInProgress } = this.state;

        // Handle list changes while batching or when additional content batching is enabled
        const shouldConsiderChildChanges =
            (isBatchedRenderInProgress || this.props.batchRenderAdditionalContent) &&
            childElementIdsHaveChanged(prevProps, this.props);

        if (shouldConsiderChildChanges) {
            const addedElements = difference(this.props.childElementIds, prevProps.childElementIds);
            const removedElements = difference(prevProps.childElementIds, this.props.childElementIds);

            if (isBatchedRenderInProgress) {
                this.applyAddedAndRemovedDuringBatch(addedElements, removedElements);
            }

            // After batched render has finished, if content has been added to the end, trigger a new batched render
            if (!isBatchedRenderInProgress) {
                this.maybeTriggerNewBatchAfterFinish(prevProps, addedElements);
            }
        }

        if (batchedRenderNoLongerInProgress(prevState, this.state) && this.props.onOrchestrationEnd) {
            this.props.onOrchestrationEnd();
        }

        // Restart the batched render
        if (this.props.batchRenderAdditionalContent && batchedRenderNowInProgress(prevState, this.state)) {
            this.restartBatchedRender(prevProps);
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    updateRenderState = (newState) => {
        const { beforeRenderBatch } = this.props;

        // The only expected state update that wouldn't trigger a new render is setting isBatchedRenderInProgress to true
        if (beforeRenderBatch && !batchedRenderNowInProgress(this.state, newState)) {
            beforeRenderBatch();
        }

        this.setState(newState);
    };

    // Applies added/removed element ids to the working lists during an in-progress batched render
    applyAddedAndRemovedDuringBatch = (addedElements, removedElements) => {
        addedElements.forEach((elementId) => {
            const index = this.props.childElementIds.indexOf(elementId);
            if (index === -1) return;
            if (index <= this.initialRenderElementIds.length - 1) {
                this.initialRenderElementIds.splice(index, 0, elementId);
                return;
            }
            this.orderedElementIds.splice(index - this.initialRenderElementIds.length, 0, elementId);
        });

        removedElements.forEach((elementId) => {
            let index = this.initialRenderElementIds.indexOf(elementId);
            if (index !== -1) this.initialRenderElementIds.splice(index, 1);
            index = this.orderedElementIds.indexOf(elementId);
            if (index !== -1) this.orderedElementIds.splice(index, 1);
        });
    };

    // If batching finished and new items were appended, trigger a new batched render cycle
    maybeTriggerNewBatchAfterFinish = (prevProps, addedElements) => {
        if (!prevProps.batchRenderAdditionalContent) return;
        if (!addedElements.length) return;
        if (!isEqual(addedElements, this.props.childElementIds.slice(-addedElements.length))) return;
        if (!this.props.childElementIds || !this.props.childElementIds.length) return;
        this.updateRenderState({ isBatchedRenderInProgress: true });
    };

    // Reset lists and counters and kick off the next batched render
    restartBatchedRender = (prevProps) => {
        if (this.props.onOrchestrationStart) this.props.onOrchestrationStart();

        const newInitialRenderSize = prevProps.childElementIds.length + this.props.initialRenderSize;
        this.initialRenderElementIds = this.props.childElementIds.slice(0, newInitialRenderSize);
        this.orderedElementIds = this.props.childElementIds.slice(this.initialRenderElementIds.length);
        this.numberOfIterations = 0;

        this.scheduleBatchedRender();
    };

    startBatchedRender = () => {
        if (this.props.onOrchestrationStart) this.props.onOrchestrationStart();

        const { initialRenderSize = DEFAULT_RENDER_INCREMENT } = this.props;

        this.initialRenderElementIds =
            this.props.childElementIds && this.props.childElementIds.length
                ? this.props.childElementIds.slice(0, initialRenderSize)
                : EMPTY_ARRAY;

        // This is the array of Ids that will be gradually added as the batched render runs
        this.orderedElementIds = this.props.childElementIds.slice(initialRenderSize);
        this.numberOfIterations = 0;

        this.scheduleBatchedRender();
    };

    scheduleBatchedRender = () => {
        if (this.unmounted) return;

        const { childElementIds, renderIncrement = DEFAULT_RENDER_INCREMENT } = this.props;
        const { elementIdsToRender } = this.state;

        this.numberOfIterations++;

        // First render
        if (this.numberOfIterations === 1) {
            this.updateRenderState({
                elementIdsToRender: this.initialRenderElementIds,
                isBatchedRenderInProgress: this.initialRenderElementIds.length < childElementIds.length,
            });

            if (this.initialRenderElementIds.length < childElementIds.length) {
                rIC(this.scheduleBatchedRender);
            }

            return;
        }

        // Too many renders, bail out
        if (this.numberOfIterations > MAX_ITERATIONS) {
            this.updateRenderState({ isBatchedRenderInProgress: false });
            return;
        }

        // Later renders
        const currentlyRenderedElementCount = elementIdsToRender.length;
        const totalElementCount = childElementIds.length;

        const newRenderCount = Math.min(currentlyRenderedElementCount + renderIncrement, totalElementCount);

        const finishedInitialRender = newRenderCount >= totalElementCount;

        if (finishedInitialRender) {
            this.updateRenderState({ isBatchedRenderInProgress: false });
        } else {
            const newElementIdsToRender = [
                ...this.initialRenderElementIds,
                ...this.orderedElementIds.slice(0, newRenderCount - this.initialRenderElementIds.length),
            ];

            this.updateRenderState({
                elementIdsToRender: newElementIdsToRender,
            });

            // Schedule another render if it's not finished rendering
            rIC(this.scheduleBatchedRender);
        }
    };

    render() {
        const { isBatchedRenderInProgress, elementIdsToRender } = this.state;
        const { childElementIds, children, skipOrchestration } = this.props;

        if (!children || typeof children !== 'function') return null;

        const showSubsetOfChildren = isBatchedRenderInProgress && !skipOrchestration;
        const renderedElementIds = showSubsetOfChildren ? elementIdsToRender : childElementIds;

        // Pass list length, so we can prevent multiple child renders by forcing the list size to the completed list
        return children(renderedElementIds, childElementIds.length);
    }
}

OrchestratedList.propTypes = {
    childElementIds: PropTypes.array,
    initialRenderSize: PropTypes.number,
    onOrchestrationStart: PropTypes.func,
    onOrchestrationEnd: PropTypes.func,
    beforeRenderBatch: PropTypes.func,
    batchRenderAdditionalContent: PropTypes.bool,
    renderIncrement: PropTypes.number,
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    skipOrchestration: PropTypes.bool,
};

export default OrchestratedList;
